import {
  JOB_PROMPT_PLACEHOLDER,
  type JobConfigFromPromptResult,
} from "@repo/adapter-definitions/job-config";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Checkbox } from "@repo/ui/checkbox";
import { DialogFooter } from "@repo/ui/dialog";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import { cn } from "@repo/ui/lib/utils";
import { Textarea } from "@repo/ui/textarea";
import { AlertCircle, Loader2, Sparkles } from "lucide-react";
import { useState } from "react";
import { generateJobConfigFromPrompt } from "@/lib/api";
import {
  canConfirm,
  describeConfigRows,
  describeOverride,
  findAssumedDefault,
  type LodgeJobInput,
  toLodgeJobInput,
} from "./describeJob";

interface Clarification {
  question: string;
  answer: string;
}

interface DescribeJobPanelProps {
  isSubmitting: boolean;
  onConfirm: (input: LodgeJobInput) => void;
  onCancel: () => void;
}

const DEFAULT_ROW = "bg-amber-50 border-amber-300 dark:bg-amber-950/40 dark:border-amber-700";

function DefaultBadge({ reason }: { reason?: string }) {
  return (
    <Badge variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-300">
      Default{reason ? ` — ${reason}` : ""}
    </Badge>
  );
}

export function DescribeJobPanel({ isSubmitting, onConfirm, onCancel }: DescribeJobPanelProps) {
  const [prompt, setPrompt] = useState("");
  const [clarifications, setClarifications] = useState<Clarification[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<JobConfigFromPromptResult | null>(null);
  const [generatedFor, setGeneratedFor] = useState("");
  const [defaultsAccepted, setDefaultsAccepted] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = async () => {
    const answered = (result?.unresolved ?? [])
      .map((u) => ({ question: u.question, answer: answers[u.question]?.trim() ?? "" }))
      .filter((c) => c.answer);
    const nextClarifications = [...clarifications, ...answered];
    setIsGenerating(true);
    setError(null);
    try {
      const next = await generateJobConfigFromPrompt(prompt, nextClarifications);
      setClarifications(nextClarifications);
      setAnswers({});
      setDefaultsAccepted(false);
      setResult(next);
      setGeneratedFor(prompt);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate the job config");
    } finally {
      setIsGenerating(false);
    }
  };

  const stale = result !== null && prompt !== generatedFor;
  const ready = result !== null && !stale && canConfirm(result, defaultsAccepted);
  const jobNameDefault = result && findAssumedDefault(result, "jobName");
  const runTypeDefault = result && findAssumedDefault(result, "runType");

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="job-prompt">Describe the job</Label>
        <Textarea
          id="job-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={JOB_PROMPT_PLACEHOLDER}
          rows={6}
          maxLength={4000}
        />
        {clarifications.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {clarifications.length} earlier answer{clarifications.length === 1 ? "" : "s"} included.{" "}
            <button type="button" className="underline" onClick={() => setClarifications([])}>
              Clear answers
            </button>
          </p>
        )}
      </div>

      {error && (
        <div className="flex gap-2 rounded-md border border-destructive/50 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {stale && (
        <p className="text-sm text-amber-700 dark:text-amber-300">
          The description changed — update the preview before confirming.
        </p>
      )}

      {result && (
        <div className="space-y-4 rounded-lg border p-4" data-testid="job-config-preview">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="font-medium">{result.jobName}</h4>
            {jobNameDefault && <DefaultBadge reason={jobNameDefault.reason} />}
          </div>

          <div className={cn("rounded-md border p-2 text-sm", runTypeDefault && DEFAULT_ROW)}>
            <span className="text-muted-foreground">When: </span>
            {result.runType === "immediate" ? (
              "Run now"
            ) : (
              <>
                Scheduled <code>{result.cronExpression}</code> (UTC)
              </>
            )}
            {runTypeDefault && (
              <span className="ml-2">
                <DefaultBadge reason={runTypeDefault.reason} />
              </span>
            )}
          </div>

          <div className="space-y-1 text-sm">
            <span className="text-muted-foreground">Where:</span>
            <div className="flex flex-wrap gap-1">
              {result.suburbs.map((s) => (
                <Badge key={s.id} variant="secondary">
                  {s.locality} {s.postcode} {s.state}
                </Badge>
              ))}
              {result.suburbLists.map((l) => (
                <Badge key={l.id} variant="outline">
                  List: {l.name}
                </Badge>
              ))}
              {result.suburbs.length + result.suburbLists.length === 0 && (
                <span className="text-muted-foreground">—</span>
              )}
            </div>
          </div>

          <div className="space-y-1 text-sm">
            <span className="text-muted-foreground">Sites:</span>
            <div className="flex flex-wrap gap-1">
              {result.adapters.map((a) => (
                <Badge key={a.id} variant="secondary">
                  {a.name}
                </Badge>
              ))}
              {result.adapters.length === 0 && <span className="text-muted-foreground">—</span>}
            </div>
          </div>

          <div className="space-y-1">
            {describeConfigRows(result).map((row) => (
              <div
                key={row.field}
                className={cn(
                  "flex flex-wrap items-center justify-between gap-2 rounded-md border p-2 text-sm",
                  row.source === "default" && DEFAULT_ROW,
                )}
              >
                <span>
                  <span className="text-muted-foreground">{row.label}: </span>
                  {row.value}
                </span>
                {row.source === "default" ? (
                  <DefaultBadge reason={row.reason} />
                ) : (
                  <Badge variant="outline">From your description</Badge>
                )}
              </div>
            ))}
            <p className="text-xs text-muted-foreground">
              Filters not listed (price, bedrooms, property type…) are not applied.
            </p>
          </div>

          {result.adapterOverrides.length > 0 && (
            <div className="space-y-1 text-sm">
              <span className="text-muted-foreground">Per-site overrides:</span>
              {result.adapterOverrides.map((o) => (
                <p key={o.adapterId}>
                  <span className="font-medium">
                    {result.adapters.find((a) => a.id === o.adapterId)?.name ?? o.adapterSlug}
                  </span>
                  : {describeOverride(o.config)}
                </p>
              ))}
            </div>
          )}

          {result.unresolved.length > 0 && (
            <div className="space-y-3 rounded-md border border-destructive/50 p-3">
              <p className="text-sm font-medium text-destructive">
                Answer these before the job can be created, then update the preview:
              </p>
              {result.unresolved.map((u, index) => (
                <div key={u.question} className="space-y-1">
                  <Label htmlFor={`unresolved-${index}`} className="text-sm font-normal">
                    {u.question}
                  </Label>
                  <Input
                    id={`unresolved-${index}`}
                    value={answers[u.question] ?? ""}
                    onChange={(e) =>
                      setAnswers((prev) => ({ ...prev, [u.question]: e.target.value }))
                    }
                  />
                </div>
              ))}
            </div>
          )}

          {result.assumedDefaults.length > 0 && result.unresolved.length === 0 && (
            <div className="flex items-start gap-2">
              <Checkbox
                id="accept-defaults"
                checked={defaultsAccepted}
                onCheckedChange={(checked) => setDefaultsAccepted(checked === true)}
              />
              <Label htmlFor="accept-defaults" className="text-sm font-normal leading-snug">
                Accept the {result.assumedDefaults.length} highlighted default
                {result.assumedDefaults.length === 1 ? "" : "s"}. To change one, say so in the
                description and update the preview.
              </Label>
            </div>
          )}
        </div>
      )}

      <DialogFooter className="flex justify-between sm:justify-between">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={result ? "outline" : "default"}
            disabled={!prompt.trim() || isGenerating}
            onClick={() => void generate()}
          >
            {isGenerating ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            {result ? "Update preview" : "Preview job"}
          </Button>
          {result && (
            <Button
              type="button"
              disabled={!ready || isSubmitting || isGenerating}
              onClick={() => onConfirm(toLodgeJobInput(result))}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {result.runType === "immediate" ? "Confirm & Run" : "Confirm & Schedule"}
            </Button>
          )}
        </div>
      </DialogFooter>
    </div>
  );
}
